import { css } from "@emotion/react";
import type { ColumnDef, SortingState, Table } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { graphql, usePaginationFragment } from "react-relay";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { jsonStringToFlatObject } from "@phoenix/utils/jsonUtils";
import { calculateTTFTMs, fmtTTFT } from "@phoenix/utils/ttftUtils";

import {
  Button,
  Flex,
  Heading,
  Icon,
  Icons,
  Link,
  Text,
  ToggleButton,
  ToggleButtonGroup,
  View,
} from "@phoenix/components";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { compactResizeHandleCSS } from "@phoenix/components/resize/styles";
import {
  AnnotationSummaryGroupTokens,
  TraceAnnotationSummaryGroupTokens,
} from "@phoenix/components/annotation/AnnotationSummaryGroup";
import { MeanScore } from "@phoenix/components/annotation/MeanScore";
import { ContextualHelp } from "@phoenix/components/core/tooltip/ContextualHelp";
import { Truncate } from "@phoenix/components/core/utility/Truncate";
import { CellWithControlsWrap, LoadMoreRow } from "@phoenix/components/table";
import { IndeterminateCheckboxCell } from "@phoenix/components/table/IndeterminateCheckboxCell";
import { selectableTableCSS } from "@phoenix/components/table/styles";
import { TextCell } from "@phoenix/components/table/TextCell";
import { TimestampCell } from "@phoenix/components/table/TimestampCell";
import { TraceTokenCosts } from "@phoenix/components/trace";
import { LatencyText } from "@phoenix/components/trace/LatencyText";
import { SpanCumulativeTokenCount } from "@phoenix/components/trace/SpanCumulativeTokenCount";
import { SpanKindToken } from "@phoenix/components/trace/SpanKindToken";
import { SpanStatusCodeIcon } from "@phoenix/components/trace/SpanStatusCodeIcon";
import { SpanTokenCosts } from "@phoenix/components/trace/SpanTokenCosts";
import { SpanTokenCount } from "@phoenix/components/trace/SpanTokenCount";
import { SELECTED_SPAN_NODE_ID_PARAM } from "@phoenix/constants/searchParams";
import { useStreamState } from "@phoenix/contexts/StreamStateContext";
import { useTracingContext } from "@phoenix/contexts/TracingContext";
import { SummaryValueLabels } from "@phoenix/pages/project/AnnotationSummary";
import { MetadataTableCell } from "@phoenix/pages/project/MetadataTableCell";
import { useTracePagination } from "@phoenix/pages/trace/TracePaginationContext";

import type {
  SpansTable_spans$key,
  SpanStatusCode,
} from "./__generated__/SpansTable_spans.graphql";
import type { SpansTableSpansQuery } from "./__generated__/SpansTableSpansQuery.graphql";
import { DEFAULT_PAGE_SIZE } from "./constants";
import { ProjectFilterConfigButton } from "./ProjectFilterConfigButton";
import { ProjectTableEmpty } from "./ProjectTableEmpty";
import { RetrievalEvaluationLabel } from "./RetrievalEvaluationLabel";
import { SpanColumnSelector } from "./SpanColumnSelector";
import { SpanFilterConditionField } from "./SpanFilterConditionField";
import { SpanSelectionToolbar } from "./SpanSelectionToolbar";
import { FilterSidebar } from "./FilterSidebar";
import { DraggableTableHeader } from "./DraggableTableHeader";
import { spansTableCSS, scrollbarCSS } from "./styles";
import { DEFAULT_SORT, getGqlSort, makeAnnotationColumnId } from "./tableUtils";

type SpansTableProps = {
  project: SpansTable_spans$key;
};

const PAGE_SIZE = DEFAULT_PAGE_SIZE;

type RootSpanFilterValue = "root" | "all";

const defaultColumnSettings = {
  minSize: 100,
} satisfies Partial<ColumnDef<unknown>>;

function isRootSpanFilterValue(val: unknown): val is RootSpanFilterValue {
  return val === "root" || val === "all";
}

const trCSS = css`
  height: 52px !important;
  max-height: 52px !important;
  cursor: pointer;
  border-left: 3px solid transparent;

  &:hover {
    background-color: var(--hover-background);
  }
  &[data-selected="true"] {
    background-color: var(--global-table-row-selected-background-color);
  }
  &[data-pii="true"] {
    background-color: #fffbeb !important;
    border-left: 3px solid var(--ac-global-color-warning) !important;
  }
  &[data-pii="true"]:hover {
    background-color: #fef3c7 !important;
  }
  &[data-pii="true"][data-selected="true"] {
    background-color: #fef3c7 !important;
  }
`;

const TableBody = <T extends { trace: { traceId: string }; id: string }>({
  table,
  hasNext,
  onLoadNext,
  isLoadingNext,
}: {
  table: Table<T>;
  hasNext: boolean;
  onLoadNext: () => void;
  isLoadingNext: boolean;
}) => {
  "use no memo";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { traceId } = useParams();
  const selectedSpanNodeId = searchParams.get(SELECTED_SPAN_NODE_ID_PARAM);
  return (
    <tbody>
      {table.getRowModel().rows.map((row) => {
        const isSelected =
          selectedSpanNodeId === row.original.id ||
          (!selectedSpanNodeId && row.original.trace.traceId === traceId);

        // Machi, row level-la PII check pannuvom 🛡️
        const metadata = (row.original as any).metadata;
        let hasPII = false;
        if (metadata) {
          const metaObj = typeof metadata === "string" ? jsonStringToFlatObject(metadata) : metadata;
          hasPII = !!(
            metaObj.evalight_pii_detected ||
            metaObj["evalight.pii_detected"] ||
            metaObj.metadata_evalight_pii_detected
          );
        }

        return (
          <tr
            key={row.id}
            data-selected={isSelected}
            data-pii={hasPII}
            css={trCSS}
            style={{ height: "52px", maxHeight: "52px" }}
            onClick={() =>
              navigate(
                `${row.original.trace.traceId}?${SELECTED_SPAN_NODE_ID_PARAM}=${row.original.id}`
              )
            }
          >
            {row.getVisibleCells().map((cell: any) => {
              const colSizeVar = `--col-${cell.column.id}-size`;
              return (
                <td
                  key={cell.id}
                  style={{
                    width: `calc(var(${colSizeVar}) * 1px)`,
                    maxWidth: `calc(var(${colSizeVar}) * 1px)`,
                    height: "52px",
                    maxHeight: "52px",
                    verticalAlign: "middle",
                    padding: "3px 8px",
                    boxSizing: "border-box",
                    // prevent all wrapping, just show an ellipsis and let users expand if necessary
                    textWrap: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <div
                    style={{
                      height: "46px",
                      maxHeight: "46px",
                      display: "flex",
                      alignItems: "center",
                      overflow: cell.column.id === "annotations" ? "visible" : "hidden",
                      width: "100%",
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </td>

              );
            })}
          </tr>
        );
      })}
      {hasNext ? (
        <LoadMoreRow
          onLoadMore={onLoadNext}
          key="load-more"
          isLoadingNext={isLoadingNext}
        />
      ) : null}
    </tbody>
  );
};

// special memoized wrapper for our table body that we will use during column resizing
export const MemoizedTableBody = React.memo(
  TableBody,
  (prev: any, next: any) => prev.table.options.data === next.table.options.data
) as typeof TableBody;

export function SpansTable(props: SpansTableProps) {
  const { fetchKey } = useStreamState();
  //we need a reference to the scrolling element for logic down below
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef<boolean>(true);
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filterCondition, setFilterCondition] = useState<string>("");
  const [rootSpansOnly, setRootSpansOnly] = useState<boolean>(true);
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(() => {
    const saved = localStorage.getItem("filterSidebarVisible");
    return saved !== null ? saved === "true" : true;
  });
  const toggleSidebar = (val: boolean) => {
    setSidebarVisible(val);
    localStorage.setItem("filterSidebarVisible", String(val));
  };
  const columnVisibility = useTracingContext((state) => state.columnVisibility);
  const { data, loadNext, hasNext, isLoadingNext, refetch } =
    usePaginationFragment<SpansTableSpansQuery, SpansTable_spans$key>(
      graphql`
        fragment SpansTable_spans on Project
        @refetchable(queryName: "SpansTableSpansQuery")
        @argumentDefinitions(
          after: { type: "String", defaultValue: null }
          first: { type: "Int", defaultValue: 30 }
          rootSpansOnly: { type: "Boolean", defaultValue: true }
          sort: { type: "SpanSort", defaultValue: { col: startTime, dir: desc } }
          filterCondition: { type: "String", defaultValue: null }
        ) {
          name
          ...SpanColumnSelector_annotations
          spans(
            first: $first
            after: $after
            sort: $sort
            rootSpansOnly: $rootSpansOnly
            filterCondition: $filterCondition
            orphanSpanAsRootSpan: $orphanSpanAsRootSpan
            timeRange: $timeRange
          ) @connection(key: "SpansTable_spans") {
            edges {
              span: node {
                id
                parentId
                spanKind
                name
                metadata
                attributes
                statusCode
                agentName
                username
                keyAlias
                modelName
                startTime
                latencyMs
                tokenCountTotal @skip(if: $rootSpansOnly)
                cumulativeTokenCountTotal @include(if: $rootSpansOnly)
                sessionId
                spanId
                descendants(first: 50) @include(if: $rootSpansOnly) {
                  edges {
                    node {
                      id
                      modelName
                      attributes
                    }
                  }
                }
                trace {
                  id
                  traceId
                  costSummary @include(if: $rootSpansOnly) {
                    total {
                      cost
                    }
                  }
                  traceAnnotations {
                    id
                    name
                    label
                    score
                    explanation
                    metadata
                    annotatorKind
                    identifier
                    user {
                      username
                      profilePictureUrl
                    }
                  }
                  traceAnnotationSummaries {
                    labelFractions {
                      fraction
                      label
                    }
                    meanScore
                    name
                  }
                }
                input {
                  value: truncatedValue
                }
                output {
                  value: truncatedValue
                }
                spanAnnotations {
                  id
                  name
                  label
                  score
                  annotatorKind
                  createdAt
                }
                spanAnnotationSummaries {
                  labelFractions {
                    fraction
                    label
                  }
                  meanScore
                  name
                }
                documentRetrievalMetrics {
                  evaluationName
                  ndcg
                  precision
                  hit
                }
                costSummary @skip(if: $rootSpansOnly) {
                  total {
                    cost
                  }
                }
                ...AnnotationSummaryGroup
              }
            }
          }
        }
      `,
      props.project
    );

  const pagination = useTracePagination();
  const setTraceSequence = pagination?.setTraceSequence;
  useEffect(() => {
    if (!setTraceSequence) {
      return;
    }
    setTraceSequence(
      data.spans.edges.map(({ span }) => ({
        traceId: span.trace.traceId,
        spanId: span.id,
      }))
    );
    return () => {
      setTraceSequence([]);
    };
  }, [data.spans.edges, setTraceSequence]);

  const annotationColumnVisibility = useTracingContext(
    (state) => state.annotationColumnVisibility
  );
  const visibleAnnotationColumnNames = useMemo(() => {
    return Object.keys(annotationColumnVisibility).filter(
      (name) => annotationColumnVisibility[name]
    );
  }, [annotationColumnVisibility]);

  const tableData = useMemo(() => {
    const tableData = data.spans.edges.map(({ span }) => span);

    return tableData;
  }, [data]);
  type TableRow = (typeof tableData)[number];

  const dynamicAnnotationColumns: ColumnDef<TableRow>[] =
    visibleAnnotationColumnNames.map((name) => {
      return {
        header: name,
        columns: [
          {
            header: `labels`,
            accessorKey: makeAnnotationColumnId(name, "label"),
            cell: ({ row }) => {
              const annotation = row.original.spanAnnotationSummaries.find(
                (annotation) => annotation.name === name
              );
              if (!annotation) {
                return null;
              }
              return (
                <SummaryValueLabels
                  name={name}
                  labelFractions={annotation.labelFractions}
                />
              );
            },
          } as ColumnDef<TableRow>,
          {
            header: `mean score`,
            accessorKey: makeAnnotationColumnId(name, "score"),
            cell: ({ row }) => {
              const annotation = row.original.spanAnnotationSummaries.find(
                (annotation) => annotation.name === name
              );
              if (!annotation) {
                return null;
              }
              return <MeanScore value={annotation.meanScore} fallback={null} />;
            },
          } as ColumnDef<TableRow>,
        ],
      };
    });

  const annotationColumns: ColumnDef<TableRow>[] = [
    {
      header: () => (
        <Flex direction="row" gap="size-50">
          <span>annotations</span>
          <ContextualHelp>
            <Heading level={3} weight="heavy">
              Annotations
            </Heading>
            <Text>
              Evaluations and human annotations logged via the API or set via
              the UI.
            </Text>
          </ContextualHelp>
        </Flex>
      ),
      id: "annotations",
      accessorKey: "spanAnnotations",
      enableSorting: false,

      cell: ({ row }) => {
        // A root span's row represents a whole run: show the canonical
        // run-level (trace) verdicts first and keep observation-level
        // annotations as the diagnostic layer beneath them. Mirror
        // annotations are excluded from root spans so the same verdict is not
        // shown twice.
        const span = row.original;
        const isRootSpan = span.parentId == null;
        const trace = span.trace;
        const traceAnnotations =
          isRootSpan && "traceAnnotations" in trace
            ? trace.traceAnnotations
            : [];
        const traceSummaries =
          isRootSpan && "traceAnnotationSummaries" in trace
            ? trace.traceAnnotationSummaries
            : [];
        return (
          <div
            style={{
              maxHeight: "46px",
              height: "46px",
              overflowY: "auto",
              overflowX: "hidden",
              width: "100%",
              scrollbarWidth: "thin",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Flex direction="row" gap="size-50" wrap="wrap">
              {traceSummaries.length > 0 ? (
                <TraceAnnotationSummaryGroupTokens
                  annotations={traceAnnotations}
                  summaries={traceSummaries}
                  showFilterActions
                />
              ) : null}
              <AnnotationSummaryGroupTokens
                span={span}
                showFilterActions
                excludeMirrorAnnotations={isRootSpan}
              />
              {row.original.documentRetrievalMetrics.map((retrievalMetric) => {
                return (
                  <React.Fragment
                    key={`${retrievalMetric.evaluationName}-${retrievalMetric.precision}`}
                  >
                    <RetrievalEvaluationLabel
                      key="ndcg"
                      name={retrievalMetric.evaluationName}
                      metric="ndcg"
                      score={retrievalMetric.ndcg}
                    />
                    <RetrievalEvaluationLabel
                      key="precision"
                      name={retrievalMetric.evaluationName}
                      metric="precision"
                      score={retrievalMetric.precision}
                    />
                    <RetrievalEvaluationLabel
                      key="hit"
                      name={retrievalMetric.evaluationName}
                      metric="hit"
                      score={retrievalMetric.hit}
                    />
                  </React.Fragment>
                );
              })}
            </Flex>
          </div>
        );
      },
    },
    ...dynamicAnnotationColumns,
  ];
  const columns: ColumnDef<TableRow>[] = [
    {
      id: "select",
      maxSize: 32,
      header: ({ table }) => (
        <IndeterminateCheckboxCell
          {...{
            isSelected: table.getIsAllRowsSelected(),
            isIndeterminate: table.getIsSomeRowsSelected(),
            onChange: table.toggleAllRowsSelected,
          }}
        />
      ),
      cell: ({ row }) => (
        <IndeterminateCheckboxCell
          {...{
            isSelected: row.getIsSelected(),
            isDisabled: !row.getCanSelect(),
            isIndeterminate: row.getIsSomeSelected(),
            onChange: row.toggleSelected,
          }}
        />
      ),
    },
    {
      header: "status",
      accessorKey: "statusCode",
      enableSorting: false,
      minSize: 75,
      maxSize: 95,
      cell: ({ getValue, row }) => {
        const statusCode = getValue() as SpanStatusCode;
        const metadata = row.original.metadata;
        let hasPII = false;
        let piiTypes = "";

        if (metadata) {
          const metaObj = typeof metadata === "string" ? jsonStringToFlatObject(metadata) : metadata;
          hasPII = !!(
            metaObj.evalight_pii_detected ||
            metaObj["evalight.pii_detected"] ||
            metaObj.metadata_evalight_pii_detected
          );
          piiTypes = String(
            metaObj.evalight_pii_types ||
            metaObj["evalight.pii_types"] ||
            metaObj.metadata_evalight_pii_types ||
            ""
          );
          // Machi, confidence level fetch pannuvom 🏷️
          const piiConfidence = String(
            metaObj.evalight_pii_confidence ||
            metaObj["evalight.pii_confidence"] ||
            metaObj.metadata_evalight_pii_confidence ||
            "MEDIUM"
          );
          (row.original as any)._piiConfidence = piiConfidence; // Save for tooltip
        }

        return (
          <Flex direction="row" gap="size-50" alignItems="center">
            <SpanStatusCodeIcon statusCode={statusCode} />
            {hasPII && (
              <Icon
                svg={<Icons.ShieldOutline />}
                color="warning"
                title={piiTypes ? `PII Detected: ${piiTypes}` : "Sensitive Data Masked"}
              />
            )}
          </Flex>
        );
      },
    },
    {
      header: "kind",
      accessorKey: "spanKind",
      maxSize: 100,
      enableSorting: false,
      cell: ({ getValue }) => {
        return <SpanKindToken spanKind={getValue() as string} />;
      },
    },
    {
      header: "name",
      accessorKey: "name",
      enableSorting: false,
      cell: ({ getValue, row }) => {
        const span = row.original;
        const { traceId } = span.trace;
        const nameVal = (getValue() as string) || "--";
        return (
          <Link
            to={`${traceId}?${SELECTED_SPAN_NODE_ID_PARAM}=${span.id}`}
            title={nameVal}
            css={css`
              font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
              font-weight: 600;
              font-size: 13px;
              color: #00685f;
              text-decoration: none;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              display: inline-block;
              max-width: 100%;
              &:hover {
                text-decoration: underline;
                color: #004d47;
              }
              .theme--dark & {
                color: #14bab6;
                &:hover {
                  color: #2dd4bf;
                }
              }
            `}
          >
            {nameVal}
          </Link>
        );
      },
    },
    {
      header: "agent name",
      accessorKey: "agentName",
      enableSorting: false,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val || val === "--") {
          return <span css={css`color: #94a3b8; font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;`}>--</span>;
        }
        return (
          <span
            title={String(val)}
            css={css`
              font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
              font-weight: 500;
              font-size: 12.5px;
              color: #0f172a;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              display: inline-block;
              max-width: 100%;
              .theme--dark & {
                color: #f8fafc;
              }
            `}
          >
            {String(val)}
          </span>
        );
      },
    },
    {
      header: "username",
      accessorKey: "username",
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val || val === "--") {
          return <span css={css`color: #94a3b8; font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;`}>--</span>;
        }
        return (
          <span
            css={css`
              font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
              font-weight: 400;
              font-size: 13px;
              color: #334155;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              display: inline-block;
              max-width: 100%;
              .theme--dark & {
                color: #cbd5e1;
              }
            `}
          >
            {String(val)}
          </span>
        );
      },
    },
    {
      header: "key alias",
      accessorKey: "keyAlias",
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const val = getValue();
        let alias = val ? String(val) : null;
        if (!alias) {
          const metadata = row.original.metadata;
          if (metadata) {
            const metaObj = typeof metadata === "string" ? jsonStringToFlatObject(metadata) : metadata;
            alias =
              metaObj.user_api_key_alias ||
              metaObj["metadata.user_api_key_alias"] ||
              metaObj.user_api_key_hash ||
              metaObj["metadata.user_api_key_hash"] ||
              null;
          }
        }
        if (!alias || alias === "--") {
          return <span css={css`color: #94a3b8; font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;`}>--</span>;
        }
        return (
          <span
            title={alias}
            css={css`
              font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
              font-weight: 400;
              font-size: 12px;
              color: #64748b;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              display: inline-block;
              max-width: 100%;
              .theme--dark & {
                color: #94a3b8;
              }
            `}
          >
            {alias}
          </span>
        );
      },
    },
    {
      header: "model",
      accessorKey: "modelName",
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const val = getValue();
        let model = val ? String(val) : null;

        if (!model) {
          const extractFromObj = (obj: any): string | null => {
            if (!obj) return null;
            const parsed = typeof obj === "string" ? jsonStringToFlatObject(obj) : obj;
            return (
              parsed["llm.model_name"] ||
              parsed["llm.model"] ||
              parsed.model_name ||
              parsed.model ||
              parsed["llm.response.model"] ||
              parsed["gen_ai.request.model"] ||
              parsed.llm_model_name ||
              null
            );
          };

          const spanAttr = extractFromObj(row.original.attributes);
          if (spanAttr) model = spanAttr;
          else {
            const metaAttr = extractFromObj(row.original.metadata);
            if (metaAttr) model = metaAttr;
            else if (row.original.descendants?.edges) {
              for (const edge of row.original.descendants.edges) {
                const child = edge.node;
                if (child.modelName) {
                  model = String(child.modelName);
                  break;
                }
                const childAttr = extractFromObj(child.attributes);
                if (childAttr) {
                  model = childAttr;
                  break;
                }
              }
            }
          }
        }

        if (!model || model === "--") {
          return <span css={css`color: #94a3b8; font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;`}>--</span>;
        }

        return (
          <span
            title={model}
            css={css`
              font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
              font-weight: 500;
              font-size: 12px;
              color: #0f172a;
              background-color: rgba(15, 23, 42, 0.05);
              padding: 2px 6px;
              border-radius: 4px;
              border: 1px solid rgba(15, 23, 42, 0.08);
              display: inline-block;
              max-width: 100%;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              .theme--dark & {
                color: #f1f5f9;
                background-color: rgba(241, 245, 249, 0.08);
                border-color: rgba(241, 245, 249, 0.12);
              }
            `}
          >
            {model}
          </span>
        );
      },
    },
    {
      header: "session id",
      accessorKey: "sessionId",
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue();
        if (!val || val === "--") {
          return <span css={css`color: #94a3b8; font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;`}>--</span>;
        }
        return (
          <span
            title={String(val)}
            css={css`
              font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
              font-weight: 500;
              font-size: 12px;
              color: #64748b;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              display: inline-block;
              max-width: 100%;
              .theme--dark & {
                color: #94a3b8;
              }
            `}
          >
            {String(val)}
          </span>
        );
      },
    },
    {
      header: "input",
      accessorKey: "input.value",
      cell: TextCell,
      enableSorting: false,
    },
    {
      header: "output",
      accessorKey: "output.value",
      cell: TextCell,
      enableSorting: false,
    },
    {
      header: "metadata",
      accessorKey: "metadata",
      cell: ({ row }) => <MetadataTableCell metadata={row.original.metadata} />,
      enableSorting: false,
    },
    ...annotationColumns, // TODO: consider hiding this column if there are no evals. For now we want people to know that there are evals
    {
      header: "start time",
      accessorKey: "startTime",
      cell: TimestampCell,
    },
    {
      header: "latency",
      accessorKey: "latencyMs",

      cell: ({ getValue }) => {
        const value = getValue();
        if (value === null || typeof value !== "number") {
          return null;
        }
        return <LatencyText latencyMs={value} />;
      },
    },
    {
      header: "TTFT",
      id: "ttft",
      cell: ({ row }: any) => {
        const ttftMs = calculateTTFTMs(row.original);
        return fmtTTFT(ttftMs);
      },
    },
    {
      header: rootSpansOnly ? "cumulative tokens" : "total tokens",
      accessorKey: rootSpansOnly
        ? "cumulativeTokenCountTotal"
        : "tokenCountTotal",
      cell: ({ row, getValue }) => {
        const value = getValue();
        if (value === null) {
          return "--";
        }
        const span = row.original;
        const tokenCountTotal = rootSpansOnly
          ? span.cumulativeTokenCountTotal
          : span.tokenCountTotal;

        if (rootSpansOnly) {
          return (
            <SpanCumulativeTokenCount
              tokenCountTotal={tokenCountTotal || 0}
              nodeId={span.id}
            />
          );
        }

        return (
          <SpanTokenCount
            tokenCountTotal={tokenCountTotal || 0}
            nodeId={span.id}
          />
        );
      },
    },
    {
      header: rootSpansOnly ? "cumulative cost" : "total cost",
      accessorKey: rootSpansOnly
        ? "trace.costSummary.total.cost"
        : "costSummary.total.cost",
      id: rootSpansOnly ? "cumulativeTokenCostTotal" : "tokenCostTotal",
      cell: ({ row, getValue }) => {
        const value = getValue();
        if (value === null || typeof value !== "number") {
          return "--";
        }
        const span = row.original;
        return rootSpansOnly ? (
          <TraceTokenCosts totalCost={value} nodeId={span.trace.id} size="S" />
        ) : (
          <SpanTokenCosts totalCost={value} spanNodeId={span.id} size="S" />
        );
      },
    },
  ];

  useEffect(() => {
    // Skip the first render. It's been loaded by the parent
    if (isFirstRender.current === true) {
      isFirstRender.current = false;
      return;
    }
    //if the sorting changes, we need to reset the pagination
    startTransition(() => {
      const sort = sorting[0];
      refetch(
        {
          sort: sort ? getGqlSort(sort) : DEFAULT_SORT,
          after: null,
          first: PAGE_SIZE,
          filterCondition,
          rootSpansOnly,
        },
        { fetchPolicy: "store-and-network" }
      );
    });
  }, [sorting, refetch, filterCondition, fetchKey, rootSpansOnly]);
  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 300px of the bottom of the table, fetch more data if there is any
        if (
          scrollHeight - scrollTop - clientHeight < 300 &&
          !isLoadingNext &&
          hasNext
        ) {
          loadNext(PAGE_SIZE);
        }
      }
    },
    [hasNext, isLoadingNext, loadNext]
  );
  const setColumnSizing = useTracingContext((state) => state.setColumnSizing);
  const columnSizing = useTracingContext((state) => state.columnSizing);

  const [columnOrder, setColumnOrder] = useState<string[]>([]);

  const table = useReactTable<TableRow>({
    data: tableData,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnSizing,
      columnOrder,
    },
    onColumnOrderChange: setColumnOrder,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: defaultColumnSettings,
    columnResizeMode: "onChange",
    manualSorting: true,
    enableRowSelection: true,
  });

  // Set initial column order if not set
  useEffect(() => {
    if (columnOrder.length === 0 && table.getAllLeafColumns().length > 0) {
      setColumnOrder(table.getAllLeafColumns().map((d: any) => d.id));
    }
  }, [table, columnOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setColumnOrder((items: string[]) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  const rows = table.getRowModel().rows;
  const selectedRows = table.getSelectedRowModel().rows;
  const selectedSpans = selectedRows.map((row) => ({
    id: row.original.id,
    traceId: row.original.trace.id,
  }));
  const clearSelection = useCallback(() => {
    setRowSelection({});
  }, [setRowSelection]);
  const isEmpty = rows.length === 0;
  const computedColumns = table.getAllColumns().filter((column) => {
    // Filter out columns that are eval groupings
    return column.columns.length === 0;
  });

  const { columnSizingInfo, columnSizing: columnSizingState } =
    table.getState();
  const getFlatHeaders = table.getFlatHeaders;
  const colLength = computedColumns.length;
  /**
   * Instead of calling `column.getSize()` on every render for every header
   * and especially every data cell (very expensive),
   * we will calculate all column sizes at once at the root table level in a useMemo
   * and pass the column sizes down as CSS variables to the <table> element.
   * @see https://tanstack.com/table/v8/docs/framework/react/examples/column-resizing-performant
   */
  const [columnSizeVars] = useMemo(() => {
    const headers = getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return [colSizes];
    // Disabled lint as per tanstack docs linked above

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getFlatHeaders, columnSizingInfo, columnSizingState, colLength]);

  return (
    <div css={spansTableCSS}>
      <View
        paddingTop="size-100"
        paddingBottom="size-100"
        paddingStart="size-200"
        paddingEnd="size-200"
        borderBottomColor="default"
        borderBottomWidth="thin"
        flex="none"
      >
        <Flex direction="row" gap="size-100" width="100%" alignItems="center">
          <Button
            css={css`
              min-width: 100px;
            `}
            onPress={() => toggleSidebar(!sidebarVisible)}
          >
            {sidebarVisible ? "Hide Filter" : "Show Filter"}
          </Button>
          <SpanFilterConditionField onValidCondition={setFilterCondition} />

          <ToggleButtonGroup
            aria-label="Toggle between root and all spans"
            selectionMode="single"
            selectedKeys={[rootSpansOnly ? "root" : "all"]}
            onSelectionChange={(selection) => {
              if (selection.size === 0) {
                return;
              }
              const selectedKey = selection.keys().next().value;
              if (isRootSpanFilterValue(selectedKey)) {
                setRootSpansOnly(selectedKey === "root");
              } else {
                throw new Error(
                  `Unknown root span filter selection: ${selectedKey}`
                );
              }
            }}
          >
            <ToggleButton aria-label="root spans" id="root">
              Root Spans
            </ToggleButton>
            <ToggleButton aria-label="all spans" id="all">
              All
            </ToggleButton>
          </ToggleButtonGroup>
          <SpanColumnSelector columns={computedColumns} query={data} />
          <ProjectFilterConfigButton />
        </Flex>
      </View>
      <PanelGroup direction="horizontal">
        {sidebarVisible && (
          <>
            <Panel defaultSize={20} minSize={15}>
              <FilterSidebar
                items={tableData as any[]}
              />
            </Panel>
            <PanelResizeHandle css={compactResizeHandleCSS} />
          </>
        )}
        <Panel>
          <div
            css={css`
              height: 100%;
              overflow: auto;
              ${scrollbarCSS};
            `}
            onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
            ref={tableContainerRef}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <table
                css={selectableTableCSS}
                style={{
                  ...columnSizeVars,
                  width: table.getTotalSize(),
                  minWidth: "100%",
                }}
              >
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <SortableContext
                      key={headerGroup.id}
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <DraggableTableHeader key={header.id} header={header} />
                        ))}
                      </tr>
                    </SortableContext>
                  ))}
                </thead>
                <TableBody
                  table={table}
                  hasNext={hasNext}
                  onLoadNext={() => loadNext(PAGE_SIZE)}
                  isLoadingNext={isLoadingNext}
                />
              </table>
            </DndContext>
          </div>
        </Panel>
      </PanelGroup>
      {selectedRows.length ? (
        <SpanSelectionToolbar
          selectedSpans={selectedSpans}
          onClearSelection={clearSelection}
        />
      ) : null}
    </div>
  );
}